import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Sparkles, Palette, Bold, Italic, Underline, Strikethrough, AlignLeft, AlignCenter, AlignRight,
  List, ListOrdered, Pipette, MoreHorizontal, Type, ImagePlus, Crop, Eraser, SlidersHorizontal, Wand2,
  Square as SquareIcon, Layers as LayersIcon, CornerUpLeft, CopyPlus, ChevronDown,
} from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * type: "text" | "image" | "shape"
 */
export default function FloatedToolbar({ type, element, onAction, onUpdate, getAnchorRect, onOpenPopover }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: 200, top: 80 });
  const [pinned, setPinned] = useState(false);
  const drag = useRef(null);

  const reposition = () => {
    if (pinned) return;
    const a = getAnchorRect?.();
    const tb = ref.current;
    if (!a || !tb) return;
    const w = tb.offsetWidth || 540;
    const h = tb.offsetHeight || 50;
    let left = a.left + a.width / 2 - w / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - w - 12));
    let top = a.top - h - 14;
    if (top < 60) top = Math.min(window.innerHeight - h - 12, a.bottom + 14);
    setPos({ left, top });
  };

  useLayoutEffect(reposition, [type, element?.id]);
  useEffect(() => {
    const onScrollResize = () => reposition();
    window.addEventListener("resize", onScrollResize);
    document.addEventListener("scroll", onScrollResize, true);
    return () => {
      window.removeEventListener("resize", onScrollResize);
      document.removeEventListener("scroll", onScrollResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, element?.id, pinned]);

  const startDrag = (e) => {
    e.preventDefault();
    const r = ref.current.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    ref.current.setPointerCapture?.(e.pointerId);
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      let left = e.clientX - drag.current.dx;
      let top = e.clientY - drag.current.dy;
      left = Math.max(8, Math.min(left, window.innerWidth - r.width - 8));
      top = Math.max(8, Math.min(top, window.innerHeight - r.height - 8));
      setPos({ left, top });
      setPinned(true);
    };
    const up = () => { drag.current = null; };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`md-toolbar-${type}`}
      role="toolbar"
      aria-label={`${type} floating toolbar`}
      style={{
        position: "fixed",
        left: pos.left,
        top: pos.top,
        zIndex: 2600,
        background: "var(--pl-surface)",
        border: "1px solid var(--pl-border)",
        boxShadow: "0 24px 70px rgba(0,0,0,0.42)",
        color: "var(--pl-text)",
        borderRadius: 16,
        padding: 6,
        display: "flex",
        alignItems: "center",
        gap: 4,
        maxWidth: "min(96vw, 1100px)",
        overflowX: "auto",
        scrollbarWidth: "none",
      }}
      className="backdrop-blur"
      onClick={(e) => e.stopPropagation()}
    >
      <Grip onPointerDown={startDrag} />
      {type === "text"  && <TextButtons element={element} onAction={onAction} onUpdate={onUpdate} />}
      {type === "image" && <ImageButtons onAction={onAction} />}
      {type === "shape" && <ShapeButtons element={element} onAction={onAction} onUpdate={onUpdate} onOpenPopover={onOpenPopover} />}
    </div>
  );
}

function Grip({ onPointerDown }) {
  return (
    <span
      onPointerDown={onPointerDown}
      title="Drag toolbar"
      style={{
        width: 12,
        alignSelf: "stretch",
        borderRadius: 999,
        backgroundImage: "radial-gradient(circle, currentColor 1.2px, transparent 1.6px)",
        backgroundSize: "6px 6px",
        opacity: 0.36,
        cursor: "grab",
        flex: "0 0 auto",
      }}
    />
  );
}

function Btn({ active, label, children, ai, iconOnly, "data-testid": testId, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        "h-9 rounded-lg inline-flex items-center justify-center gap-1.5 transition border text-[12px] font-bold",
        iconOnly ? "w-9 px-0" : "px-2.5",
        ai
          ? "text-[#001b18] border-transparent"
          : active
          ? "text-[var(--pl-aqua)] bg-[var(--pl-aqua)]/10 border-[var(--pl-aqua)]/40"
          : "text-[var(--pl-text)] bg-white/[0.03] border-[var(--pl-border)] hover:text-[var(--pl-aqua)] hover:border-[var(--pl-aqua)]/40"
      )}
      style={ai ? { background: "linear-gradient(135deg,#27E6D1,#B9FF2F)" } : undefined}
    >
      {children}
    </button>
  );
}

/* ---------- Text ---------- */
function TextButtons({ element, onAction, onUpdate }) {
  const size = element.style.fontSize || 24;
  return (
    <>
      <button
        data-testid="md-text-font"
        className="h-9 px-3 rounded-lg text-[12px] font-bold border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)] inline-flex items-center gap-2 hover:border-[var(--pl-aqua)]/40"
      >
        <Type className="h-3.5 w-3.5 opacity-60" />
        <span style={{ fontFamily: element.style.fontFamily || "Inter" }}>{(element.style.fontFamily || "Inter").split(",")[0]}</span>
        <ChevronDown className="h-3 w-3 opacity-50" />
      </button>
      <div className="h-9 inline-flex items-center rounded-lg border border-[var(--pl-border)] bg-white/[0.03]">
        <button
          data-testid="md-text-size-minus"
          onClick={(e) => { e.stopPropagation(); onUpdate({ style: { fontSize: Math.max(8, size - 2) } }); }}
          className="w-7 h-9 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
        >–</button>
        <span className="px-1 min-w-[28px] text-center text-[12px] font-bold">{Math.round(size)}</span>
        <button
          data-testid="md-text-size-plus"
          onClick={(e) => { e.stopPropagation(); onUpdate({ style: { fontSize: Math.min(160, size + 2) } }); }}
          className="w-7 h-9 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
        >+</button>
      </div>
      <Btn ai label="Generate AI" data-testid="md-tb-generate" onClick={(e) => onAction("generate", e.currentTarget)}>
        <Sparkles className="h-3.5 w-3.5" /><span>Generate AI</span>
      </Btn>
      <Btn label="Appearance" data-testid="md-tb-appearance" onClick={() => onAction("appearance")}>
        <Palette className="h-3.5 w-3.5" /><span>Appearance</span>
      </Btn>
      <Btn iconOnly label="Bold"        active={element.style.fontWeight >= 700} data-testid="md-tb-bold"
        onClick={() => onUpdate({ style: { fontWeight: (element.style.fontWeight || 400) >= 700 ? 500 : 800 } })}>
        <Bold className="h-3.5 w-3.5" />
      </Btn>
      <Btn iconOnly label="Italic"      active={element.style.fontStyle === "italic"} data-testid="md-tb-italic"
        onClick={() => onUpdate({ style: { fontStyle: element.style.fontStyle === "italic" ? "normal" : "italic" } })}>
        <Italic className="h-3.5 w-3.5" />
      </Btn>
      <Btn iconOnly label="Underline"   data-testid="md-tb-underline"
        onClick={() => onUpdate({ style: { textDecoration: element.style.textDecoration === "underline" ? "none" : "underline" } })}>
        <Underline className="h-3.5 w-3.5" />
      </Btn>
      <Btn iconOnly label="Strikethrough" data-testid="md-tb-strike"
        onClick={() => onUpdate({ style: { textDecoration: element.style.textDecoration === "line-through" ? "none" : "line-through" } })}>
        <Strikethrough className="h-3.5 w-3.5" />
      </Btn>
      <Btn iconOnly label="Case" data-testid="md-tb-case"
        onClick={() => onUpdate({ style: { textTransform: element.style.textTransform === "uppercase" ? "none" : "uppercase" } })}>
        aA
      </Btn>
      <Btn iconOnly label="Align left"   data-testid="md-tb-align-left"
        onClick={() => onUpdate({ style: { textAlign: "left" } })}><AlignLeft className="h-3.5 w-3.5" /></Btn>
      <Btn iconOnly label="Align center" data-testid="md-tb-align-center"
        onClick={() => onUpdate({ style: { textAlign: "center" } })}><AlignCenter className="h-3.5 w-3.5" /></Btn>
      <Btn iconOnly label="Align right"  data-testid="md-tb-align-right"
        onClick={() => onUpdate({ style: { textAlign: "right" } })}><AlignRight className="h-3.5 w-3.5" /></Btn>
      <Btn iconOnly label="Bullets"   data-testid="md-tb-bullets"  onClick={() => onAction("bullets")}><List className="h-3.5 w-3.5" /></Btn>
      <Btn iconOnly label="Numbering" data-testid="md-tb-numbered" onClick={() => onAction("numbered")}><ListOrdered className="h-3.5 w-3.5" /></Btn>
      <Btn iconOnly label="Copy style" data-testid="md-tb-copy-style" onClick={() => onAction("copy-style")}><Pipette className="h-3.5 w-3.5" /></Btn>
      <Divider />
      <Btn label="Effects"      data-testid="md-tb-effects"  onClick={() => onAction("effects")}><span>Effects</span></Btn>
      <Btn label="Animate"      data-testid="md-tb-animate"  onClick={() => onAction("animate")}><span>Animate</span></Btn>
      <Btn label="Smart Layers" data-testid="md-tb-layers"   onClick={() => onAction("layers")}><LayersIcon className="h-3.5 w-3.5" /><span>Smart Layers</span></Btn>
      <Btn iconOnly label="More" data-testid="md-tb-more" onClick={() => onAction("more")}><MoreHorizontal className="h-3.5 w-3.5" /></Btn>
    </>
  );
}

/* ---------- Image ---------- */
function ImageButtons({ onAction }) {
  return (
    <>
      <Btn ai label="Generate AI" data-testid="md-tb-generate" onClick={(e) => onAction("generate", e.currentTarget)}>
        <Sparkles className="h-3.5 w-3.5" /><span>Generate AI</span>
      </Btn>
      <Btn label="Enhance" data-testid="md-tb-enhance" onClick={() => onAction("enhance")}><Wand2 className="h-3.5 w-3.5" /><span>Enhance</span></Btn>
      <Btn label="Remove"  data-testid="md-tb-remove"  onClick={() => onAction("remove")}><Eraser className="h-3.5 w-3.5" /><span>Remove</span></Btn>
      <Btn label="Crop"    data-testid="md-tb-crop"    onClick={() => onAction("crop")}><Crop className="h-3.5 w-3.5" /><span>Crop</span></Btn>
      <Btn iconOnly label="Adjust" data-testid="md-tb-adjust" onClick={() => onAction("adjust")}><SlidersHorizontal className="h-3.5 w-3.5" /></Btn>
      <Btn label="Replace" data-testid="md-tb-replace" onClick={() => onAction("replace")}><ImagePlus className="h-3.5 w-3.5" /><span>Replace</span></Btn>
      <Divider />
      <Btn label="Appearance"  data-testid="md-tb-appearance" onClick={() => onAction("appearance")}><Palette className="h-3.5 w-3.5" /><span>Appearance</span></Btn>
      <Btn label="Effects"     data-testid="md-tb-effects"    onClick={() => onAction("effects")}><span>Effects</span></Btn>
      <Btn label="Animate"     data-testid="md-tb-animate"    onClick={() => onAction("animate")}><span>Animate</span></Btn>
      <Btn label="Smart Layers" data-testid="md-tb-layers"    onClick={() => onAction("layers")}><LayersIcon className="h-3.5 w-3.5" /><span>Smart Layers</span></Btn>
      <Btn iconOnly label="More" data-testid="md-tb-more" onClick={() => onAction("more")}><MoreHorizontal className="h-3.5 w-3.5" /></Btn>
    </>
  );
}

/* ---------- Shape ---------- */
function ShapeButtons({ element, onAction, onUpdate, onOpenPopover }) {
  const radius = element.style.borderRadius ?? 18;
  const openPop = (kind) => (e) => { e.stopPropagation(); onOpenPopover?.(kind, e.currentTarget); };
  return (
    <>
      <Btn label="Fill" data-testid="md-tb-fill" onClick={openPop("fill")}>
        <SquareIcon className="h-3.5 w-3.5" /><span>Fill</span><ChevronDown className="h-3 w-3 opacity-50" />
      </Btn>
      <Btn label="Stroke" data-testid="md-tb-stroke" onClick={openPop("stroke")}>
        <span style={{
          display: "inline-block", width: 14, height: 14, borderRadius: 4,
          border: "2px solid " + (element.style.borderColor || "var(--pl-aqua)"),
        }} />
        <span>Stroke</span><ChevronDown className="h-3 w-3 opacity-50" />
      </Btn>
      <Btn label="Stroke width" data-testid="md-tb-stroke-w" onClick={openPop("stroke")}>
        {element.style.borderWidth || 1} px
      </Btn>
      <Btn label="Stroke style" data-testid="md-tb-stroke-s" onClick={openPop("stroke")}>
        <span style={{
          width: 36, height: 0,
          borderTop: `3px ${element.style.borderStyle || "solid"} currentColor`,
          opacity: 0.85,
        }} />
        <ChevronDown className="h-3 w-3 opacity-50" />
      </Btn>
      <Btn iconOnly label="Corner radius" data-testid="md-tb-radius" onClick={openPop("radius")}>
        <CornerUpLeft className="h-3.5 w-3.5" />
      </Btn>
      <Btn iconOnly label="Duplicate" data-testid="md-tb-duplicate" onClick={() => onAction("duplicate")}>
        <CopyPlus className="h-3.5 w-3.5" />
      </Btn>
      <Btn ai label="Glass" data-testid="md-tb-glass" onClick={openPop("glass")}>
        <Sparkles className="h-3.5 w-3.5" /><span>Glass</span>
      </Btn>
      <Btn label="Blur" data-testid="md-tb-blur" onClick={openPop("blur")}>
        <Sparkles className="h-3.5 w-3.5" /><span>Blur</span>
      </Btn>
      <Btn iconOnly label="More" data-testid="md-tb-more" onClick={openPop("properties")}>
        <MoreHorizontal className="h-3.5 w-3.5" />
      </Btn>
    </>
  );
}

function Divider() {
  return <span style={{ width: 1, height: 24, background: "var(--pl-border)", margin: "0 4px", flex: "0 0 1px" }} />;
}
